import {
  buildOverlayStyleForRect,
  type ElementRect,
  type OverlayGeometry,
  type OverlayPositionStyle,
} from "./editorRelativeRect.ts";
import { applyResizeDelta, type ResizeHandle } from "./resizeDelta.ts";
import { projectScreenDeltaToLocal } from "./transformMatrix.ts";

export interface ApplyResizeScreenDeltaParams {
  geometry: OverlayGeometry;
  handle: ResizeHandle;
  /** Drag delta in screen-space (zoomed) pixels — typically from a pointer-move event. */
  delta: { x: number; y: number };
  minSize?: number;
}

export interface ResizePreview {
  /** The new intrinsic-space rect after applying the local-converted delta. */
  rect: ElementRect;
  /** Pre-built overlay style for rendering a preview border. */
  overlayStyle: OverlayPositionStyle;
}

/**
 * Apply a screen-space resize delta to a geometry's intrinsic rect. Projects
 * the screen delta onto the element's local axes (using the geometry's
 * rotation + scale) so a screen-x drag on a rotated element extends the
 * element's local x edge — the visual axis the user grabbed. Returns a new
 * intrinsic-space rect packaged with a ready-to-spread overlay style.
 *
 * Limitation: the screen→local projection assumes rotation + axis-aligned
 * scale (no skew). For skewed elements the projection diverges from the
 * pointer; today's resize UX targets rotate+scale, so this is an accepted
 * tradeoff.
 */
export const applyResizeScreenDelta = ({
  geometry,
  handle,
  delta,
  minSize,
}: ApplyResizeScreenDeltaParams): ResizePreview => {
  const localDelta = projectScreenDeltaToLocal(delta, geometry.rotation, geometry.scaleX, geometry.scaleY);
  const cos = Math.cos(geometry.rotation);
  const sin = Math.sin(geometry.rotation);
  // intrinsicRect mixes units: width/height are intrinsic (pre-transform) but
  // top/left are in container/screen coords (post-transform). Run the resize
  // math in pure intrinsic space, then project the local-space top/left shift
  // back into screen coords through the linear matrix before composing the
  // overlay style.
  const localResized = applyResizeDelta({
    rect: { top: 0, left: 0, width: geometry.intrinsicRect.width, height: geometry.intrinsicRect.height },
    handle,
    delta: localDelta,
    minSize,
  });
  const screenShiftX = cos * localResized.left * geometry.scaleX - sin * localResized.top * geometry.scaleY;
  const screenShiftY = sin * localResized.left * geometry.scaleX + cos * localResized.top * geometry.scaleY;
  const rect: ElementRect = {
    top: geometry.intrinsicRect.top + screenShiftY,
    left: geometry.intrinsicRect.left + screenShiftX,
    width: localResized.width,
    height: localResized.height,
  };
  return { rect, overlayStyle: buildOverlayStyleForRect(rect, geometry) };
};

export interface CloneOverlayStyle {
  /** Composed `translate(...) M` (or `"none"`) — apply with `transform-origin: 0 0`. */
  transform: string;
  transformOrigin: "0 0";
  /** Intrinsic width to set on the clone (so flex/fill children don't expand). */
  width: number;
  /** Intrinsic height to set on the clone. */
  height: number;
}

/**
 * Style for a clone whose container is anchored at the element's bounding-rect
 * top-left (e.g. dnd-kit's `DragOverlay`). Bakes the AABB-min compensation
 * into a `translate(...)` so the clone's visual AABB lands at (0, 0) of the
 * container.
 */
export const getOverlayCloneStyle = (geometry: OverlayGeometry): CloneOverlayStyle => {
  const offsetX = geometry.intrinsicRect.left - geometry.boundingRect.left;
  const offsetY = geometry.intrinsicRect.top - geometry.boundingRect.top;
  const translate = offsetX !== 0 || offsetY !== 0 ? `translate(${offsetX}px, ${offsetY}px)` : "";
  const matrix = geometry.transform !== "none" ? geometry.transform : "";
  const transform = [translate, matrix].filter(Boolean).join(" ") || "none";
  return {
    transform,
    transformOrigin: "0 0",
    width: geometry.intrinsicRect.width,
    height: geometry.intrinsicRect.height,
  };
};
